import { useState } from 'react';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import { useMyCatalogTxns, type CatalogTxnStatus } from '@/lib/queries';
import { formatINR } from '@kollez/shared';

/**
 * /me/transactions (Page 68/N) — Pool-2 catalog charges to the student.
 *
 * PublicCatalogTransaction is seeded (dummy-fill-empties.ts). API is
 * /students/me/transactions with paginated rows + status rollup. No page
 * consumed it — students had no way to see what Kollez charged them
 * for (paid workflow stages, certification exams, etc.). DPDP §6
 * transparency: they must be able to see it.
 */

const STATUS_LABEL: Record<CatalogTxnStatus, string> = {
  pending: 'Pending',
  succeeded: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
};

function statusTone(s: CatalogTxnStatus): string {
  if (s === 'succeeded') return 'badge-success';
  if (s === 'refunded') return 'badge-info';
  if (s === 'failed') return 'badge-danger';
  return 'badge-warning';
}

function formatIsoDay(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function StudentTransactionsPage() {
  const [status, setStatus] = useState<CatalogTxnStatus | ''>('');
  const list = useMyCatalogTxns({ status, sortBy: 'initiatedAt', sortDir: 'desc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Transactions</h1>
          <p className="subhead">
            Every payment on your account. Kollez only charges for paid workflow stages
            (specific certifications, some assessments). All other Kollez features are free
            for students.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Total paid</span>
            <span className="kpi-value">{formatINR(summary.totalPaidInr)}</span>
            <span className="kpi-meta">lifetime</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Paid transactions</span>
            <span className="kpi-value">{summary.byStatus.succeeded?.count ?? 0}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Pending</span>
            <span className="kpi-value">{summary.byStatus.pending?.count ?? 0}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Refunded</span>
            <span className="kpi-value">{summary.byStatus.refunded?.count ?? 0}</span>
          </div>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as CatalogTxnStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[CatalogTxnStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length} transaction{(list.data?.meta?.total ?? rows.length) === 1 ? '' : 's'}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No transactions"
          emptyHint="You haven't been charged for anything on Kollez. Free features (browsing drives, applying, taking mock interviews) don't create transactions."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>What</th>
                  <th className="hide-mobile">Partner</th>
                  <th>Payment</th>
                  <th className="r">Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t._id}>
                    <td className="cell-sub">{formatIsoDay(t.initiatedAt)}</td>
                    <td>
                      <div className="cell-title">
                        {typeof t.opportunity === 'object' && t.opportunity?.title
                          ? t.opportunity.title
                          : t.stageKey ?? 'Catalog transaction'}
                      </div>
                      {t.chargeOn && <div className="cell-sub">Charge on: {t.chargeOn}</div>}
                    </td>
                    <td className="hide-mobile cell-sub">
                      {typeof t.partner === 'object' && t.partner?.name ? t.partner.name : '—'}
                    </td>
                    <td className="cell-sub">{t.paymentMethod ?? '—'}</td>
                    <td className="r">{formatINR(t.amountInr)}</td>
                    <td>
                      <span className={`badge ${statusTone(t.status)}`}>{STATUS_LABEL[t.status]}</span>
                      {t.status === 'succeeded' && t.settledAt && (
                        <div className="cell-sub mt-0.5">on {formatIsoDay(t.settledAt)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Refunds.</b> If a paid stage was mis-charged (duplicate, wrong amount, refunded but not
        shown here), file a grievance under DPDP §13 — the reviewer will reconcile.
      </Notice>
    </div>
  );
}
